import { config } from '../config';
import { graphFetch, mapHttpError, readError, GraphError } from './graph';
import { log } from './logger';

export type FolderEnsureResult = {
  id: string;
  name: string;
  action: 'exists' | 'created';
};

function signatureItemPath(): string {
  const folder = encodeURIComponent(config.signatureFolderName);
  const file = encodeURIComponent(config.signatureFileName);
  return `/me/drive/root:/${folder}/${file}`;
}

/**
 * Ensures /SignatureApp exists in the signed-in user's personal OneDrive.
 * Existing folder is not an error.
 */
export async function ensureSignatureFolder(accessToken: string): Promise<FolderEnsureResult> {
  const folderName = config.signatureFolderName;
  const encodedPath = encodeURIComponent(folderName);

  const existing = await graphFetch(`/me/drive/root:/${encodedPath}`, accessToken);
  if (existing.ok) {
    const data = (await existing.json()) as { id: string; name: string };
    log('SignatureApp Ordner vorhanden', { id: data.id });
    return { id: data.id, name: data.name || folderName, action: 'exists' };
  }

  if (existing.status !== 404) {
    throw mapHttpError(existing, await readError(existing));
  }

  log('SignatureApp Ordner fehlt – wird erstellt…');
  const create = await graphFetch('/me/drive/root/children', accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: folderName,
      folder: {},
      '@microsoft.graph.conflictBehavior': 'fail',
    }),
  });

  if (create.ok) {
    const data = (await create.json()) as { id: string; name: string };
    log('SignatureApp Ordner erstellt', { id: data.id });
    return { id: data.id, name: data.name || folderName, action: 'created' };
  }

  // Race: folder created between GET and POST
  if (create.status === 409) {
    const again = await graphFetch(`/me/drive/root:/${encodedPath}`, accessToken);
    if (again.ok) {
      const data = (await again.json()) as { id: string; name: string };
      log('SignatureApp Ordner vorhanden (nach Konflikt)', { id: data.id });
      return { id: data.id, name: data.name || folderName, action: 'exists' };
    }
  }

  const detail = await readError(create);
  if (create.status === 403) {
    throw new GraphError(
      'Keine Berechtigung zum Erstellen des SignatureApp-Ordners. Files.ReadWrite und Admin Consent prüfen.',
      'forbidden',
      403,
      detail,
    );
  }
  throw mapHttpError(create, detail);
}

/** True if /SignatureApp/signature.png exists (metadata GET, no content). */
export async function signatureExists(accessToken: string): Promise<boolean> {
  const res = await graphFetch(signatureItemPath(), accessToken);
  if (res.ok) {
    log('signature.png vorhanden');
    return true;
  }
  if (res.status === 404) {
    log('signature.png fehlt');
    return false;
  }
  throw mapHttpError(res, await readError(res));
}

/**
 * Downloads signature.png as Blob via Graph (authenticated).
 * No sharing links; caller creates a temporary object URL for preview.
 */
export async function downloadSignaturePng(accessToken: string): Promise<Blob> {
  const res = await graphFetch(`${signatureItemPath()}:/content`, accessToken, {
    headers: { Accept: 'image/png,*/*' },
  });
  if (!res.ok) {
    throw mapHttpError(res, await readError(res));
  }
  const blob = await res.blob();
  log('signature.png geladen', { size: blob.size, type: blob.type || 'unknown' });
  return blob;
}

/** Uploads / overwrites /SignatureApp/signature.png (PNG bytes only). */
export async function uploadSignaturePng(accessToken: string, png: Blob | ArrayBuffer): Promise<void> {
  const body = png instanceof ArrayBuffer ? png : await png.arrayBuffer();
  if (body.byteLength > config.maxSignatureBytes) {
    throw new GraphError(
      `Datei zu groß (max. ${Math.round(config.maxSignatureBytes / (1024 * 1024))} MB).`,
      'unknown',
    );
  }

  const res = await graphFetch(`${signatureItemPath()}:/content`, accessToken, {
    method: 'PUT',
    headers: {
      'Content-Type': 'image/png',
      Accept: 'application/json',
    },
    body,
  });

  if (!res.ok) {
    throw mapHttpError(res, await readError(res));
  }
  log('signature.png hochgeladen', { bytes: body.byteLength });
}

/** Deletes /SignatureApp/signature.png if present. */
export async function deleteSignaturePng(accessToken: string): Promise<void> {
  const res = await graphFetch(signatureItemPath(), accessToken, { method: 'DELETE' });
  if (res.ok || res.status === 204 || res.status === 404) {
    log('signature.png gelöscht (oder fehlte)');
    return;
  }
  throw mapHttpError(res, await readError(res));
}

/**
 * Saves a signed file under /SignatureApp/{fileName} in the user's OneDrive.
 * Overwrites if the same name already exists.
 * Returns Graph webUrl (authenticated OneDrive link, no public share).
 */
export async function uploadSignedPdf(
  accessToken: string,
  fileName: string,
  pdfBytes: Uint8Array,
): Promise<{ name: string; webUrl: string | null }> {
  const safe = fileName.replace(/[\\/:*?"<>|]/g, '_').trim() || 'Dokument-signiert.pdf';
  const folder = encodeURIComponent(config.signatureFolderName);
  const file = encodeURIComponent(safe);
  const lower = safe.toLowerCase();
  const contentType = lower.endsWith('.docx')
    ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    : 'application/pdf';
  const res = await graphFetch(`/me/drive/root:/${folder}/${file}:/content`, accessToken, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
      Accept: 'application/json',
    },
    body: new Blob([new Uint8Array(pdfBytes)], { type: contentType }),
  });
  if (!res.ok) {
    throw mapHttpError(res, await readError(res));
  }
  let webUrl: string | null = null;
  try {
    const data = (await res.json()) as { webUrl?: string; name?: string };
    webUrl = data.webUrl || null;
  } catch {
    webUrl = null;
  }
  log('Signierte Datei in OneDrive gespeichert', {
    name: safe,
    bytes: pdfBytes.byteLength,
    hasWebUrl: Boolean(webUrl),
  });
  return { name: safe, webUrl };
}
